import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Trophy, Zap, HelpCircle } from "lucide-react";
import { EventCard, isPast } from "@/pages/PublicEvents";
import { setMeta, setCanonical, injectLd, clearLd } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.picklebullpen.com";

export const CITIES = {
  bradenton: { name: "Bradenton", region: "Manatee County", match: ["bradenton"], blurb: "Home base of the Pickle Bullpen — Bradenton is where the Bullpen's leagues, Moneyball events and sanctioned tournaments run all year." },
  sarasota: { name: "Sarasota", region: "Sarasota County", match: ["sarasota"], blurb: "Sarasota players are minutes from every Pickle Bullpen tournament — compete, earn PSI, and climb the PBR rankings on the Suncoast." },
  tampa: { name: "Tampa", region: "Tampa Bay", match: ["tampa"], blurb: "Tampa Bay's competitive pickleball scene meets the Bullpen — brackets, Moneyball purses and ranked play worth the short drive south." },
  "lakewood-ranch": { name: "Lakewood Ranch", region: "Manatee County", match: ["lakewood"], blurb: "Lakewood Ranch players: your closest path to sanctioned brackets, PSI ratings and Moneyball pickleball is the Pickle Bullpen." },
  ellenton: { name: "Ellenton", region: "Manatee County", match: ["ellenton"], blurb: "Ellenton is Bullpen country — local courts, local rivals, and tournaments where every match feeds your rating." },
  "st-petersburg": { name: "St. Petersburg", region: "Pinellas County", match: ["st. pete", "st pete", "petersburg"], blurb: "St. Pete players cross the bay for Bullpen brackets — ranked tournaments, Moneyball events and a player card with your name on it." },
};

const cityFaq = (c) => [
  { q: `Where can I play pickleball tournaments in ${c.name}?`,
    a: `Pickle Bullpen runs sanctioned pickleball tournaments, leagues and Moneyball events serving ${c.name} and the greater ${c.region} area. Every upcoming event with dates, divisions, entry fees and live registration counts is listed on this page.` },
  { q: `How do I enter a pickleball tournament near ${c.name}?`,
    a: `Create a free Pickle Bullpen player profile, get your PSI (Player Skill Index) rating, and register for any open event in one tap. Skill divisions from Low Intermediate to Pro keep every bracket competitive.` },
  { q: `Do these tournaments have official ratings and rankings?`,
    a: `Yes — every match feeds your PSI rating and PBR ranking points. Podium finishes unlock State and National level events, and champions are featured on the public Champions Wall and photo gallery.` },
];

export default function CityTournaments() {
  const { city } = useParams();
  const c = CITIES[city];
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (!c) return;
    axios.get(`${API}/public/events`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
    const canonical = `${SITE}/pickleball-tournaments/${city}`;
    const title = `Pickleball Tournaments in ${c.name}, FL — Dates, Divisions & Registration | Pickle Bullpen`;
    const desc = `Find pickleball tournaments in ${c.name}, Florida: upcoming dates, skill divisions, entry fees, Moneyball purses and instant online registration. Ranked play with PSI ratings and PBR points — serving ${c.name} and ${c.region}.`;
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("name", "keywords", `pickleball tournaments ${c.name}, pickleball ${c.name} FL, pickleball events near ${c.name}, ${c.name} pickleball brackets, moneyball pickleball ${c.name}, pickleball leagues ${c.region}`);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonical);
    setCanonical(canonical);
    injectLd("city", [
      { "@context": "https://schema.org", "@type": "CollectionPage", name: title, url: canonical, description: desc,
        about: { "@type": "Thing", name: `Pickleball tournaments in ${c.name}, Florida` } },
      { "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: cityFaq(c).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Pickle Bullpen", item: SITE },
        { "@type": "ListItem", position: 2, name: "Pickleball Tournaments & Events", item: `${SITE}/events` },
        { "@type": "ListItem", position: 3, name: `Pickleball Tournaments in ${c.name}`, item: canonical } ] },
    ]);
    return () => clearLd("city");
  }, [city]); // eslint-disable-line

  if (!c) return <Navigate to="/events" replace />;
  if (!events) return null;
  const inCity = events.filter((t) => c.match.some((m) => `${t.location || ""} ${t.venue || ""}`.toLowerCase().includes(m)));
  const cityUpcoming = inCity.filter((t) => !isPast(t));
  const cityPast = inCity.filter((t) => isPast(t)).reverse().slice(0, 6);
  const nearby = events.filter((t) => !isPast(t) && !inCity.includes(t)).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="city-tournaments-page">
      <div className="max-w-5xl mx-auto">
        <nav className="mb-8 text-xs uppercase tracking-widest text-zinc-500">
          <Link to="/" className="hover:text-lime-400 transition-colors">Pickle Bullpen</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <Link to="/events" data-testid="city-back-link" className="hover:text-lime-400 transition-colors inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Events</Link>
        </nav>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-4 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {c.name} · {c.region} · Florida</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-3" data-testid="city-title">
          Pickleball Tournaments in {c.name}
        </h1>
        <p className="text-zinc-400 max-w-2xl mb-10">{c.blurb} Every event below shows dates, skill divisions, entry fees and live registration — and every match you play feeds your Player Skill Index (PSI) and PBR ranking.</p>

        {cityUpcoming.length > 0 ? (
          <div className="mb-14" data-testid="city-upcoming">
            <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl mb-6">Upcoming in {c.name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cityUpcoming.map((t) => <EventCard key={t.id} t={t} />)}
            </div>
          </div>
        ) : (
          <div className="mb-14 border border-dashed border-zinc-800 p-10 text-center" data-testid="city-empty">
            <p className="text-zinc-500 uppercase tracking-widest text-sm mb-2">No {c.name} events posted right now</p>
            <p className="text-zinc-600 text-sm">New tournaments drop here first — check the nearby events below or create a profile to get alerts.</p>
          </div>
        )}

        {nearby.length > 0 && (
          <div className="mb-14" data-testid="city-nearby">
            <h2 className="font-display font-extrabold uppercase tracking-wide text-2xl mb-2">Nearby Pickleball Events</h2>
            <p className="text-zinc-500 text-sm mb-6">Upcoming tournaments a short drive from {c.name} — all on the Bullpen circuit.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {nearby.map((t) => <EventCard key={t.id} t={t} />)}
            </div>
          </div>
        )}

        {cityPast.length > 0 && (
          <div className="mb-14" data-testid="city-past">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide text-zinc-400 text-2xl mb-6"><Trophy className="w-5 h-5" /> Recent {c.name} Results</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cityPast.map((t) => <EventCard key={t.id} t={t} />)}
            </div>
          </div>
        )}

        <div className="mb-12 border border-zinc-800 bg-white/[0.02] p-5" data-testid="city-why">
          <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-lime-300 text-lg mb-2"><Zap className="w-4 h-4" /> Why {c.name} Players Choose the Bullpen</h2>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Pickle Bullpen is a full pickleball performance ecosystem serving {c.name} and {c.region}: sanctioned tournaments with
            live brackets and big-screen court boards, a <strong className="text-zinc-200">Player Skill Index (PSI)</strong> rating that updates after every match,
            PBR ranking points with a Road to Nationals, Moneyball cash events, AI video analysis, coaching, and collectible player cards.
            Win here and your name goes on the Champions Wall.
          </p>
        </div>

        <div className="mb-12" data-testid="city-faq">
          <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-lg mb-4"><HelpCircle className="w-4 h-4 text-lime-400" /> Pickleball in {c.name} — FAQ</h2>
          <div className="space-y-3">
            {cityFaq(c).map((f, i) => (
              <details key={i} className="border border-zinc-800 bg-white/[0.02] group" data-testid={`city-faq-${i}`}>
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-bold flex items-center justify-between">
                  {f.q}<span className="text-zinc-600 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="px-4 pb-4 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="text-center border-t border-zinc-900 pt-10 mb-10">
          <p className="text-zinc-400 mb-4">Ready to compete in {c.name}? Create your free player profile and register in minutes.</p>
          <Link to="/auth?mode=register" data-testid="city-join-btn"
            className="inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider px-8 py-3 hover:bg-lime-300 transition-colors">
            Enter the Bullpen
          </Link>
        </div>

        <div className="pt-6 border-t border-zinc-900 text-xs text-zinc-500 flex flex-wrap gap-x-5 gap-y-2" data-testid="city-links">
          {Object.entries(CITIES).filter(([slug]) => slug !== city).map(([slug, cc]) => (
            <Link key={slug} to={`/pickleball-tournaments/${slug}`} className="hover:text-lime-400 transition-colors">Pickleball Tournaments in {cc.name}</Link>
          ))}
          <Link to="/events" className="hover:text-lime-400 transition-colors">All Events</Link>
          <Link to="/gallery" className="hover:text-lime-400 transition-colors">Photo Gallery</Link>
        </div>
      </div>
    </div>
  );
}
